/* global React, Icon, IconButton, Button, useState, useEffect, useRef, t, toast */
// ============================================================
// DIALOG MELLOM BYRÅ OG KLIENT
// ============================================================
// ÉN komponent for begge sider. Byrået åpner den fra klientoversikt-raden,
// klienten fra hjemsiden — samme tråd, samme markup, bare ulik `side`.
//
// Grunnen til at det ikke er to komponenter: en chat der de to partene ser hver
// sin implementasjon er en chat der de før eller siden ser forskjellig innhold.
// Her er «meg» det eneste som skiller dem.
//
// Tråden ligger under FORETAKET (`businesses/{id}/firmChats/{firmId}`). Byråets
// folk får materialisert medlemskap i foretaket når oppdraget aksepteres, og
// mister det når det avsluttes — så tilgangen følger oppdraget av seg selv.
// Se firestore.rules og docs/features/regnskapsbyra-workspace-backlog.md.

const fcTime = (isoString) => {
  const value = Date.parse(String(isoString || ''));
  if (!Number.isFinite(value)) return '';
  const date = new Date(value);
  const sameDay = new Date().toDateString() === date.toDateString();
  return sameDay
    ? date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' }) + ' '
      + date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

// Uleste for MIN side: siste melding kom fra den andre, og den kom etter at jeg
// sist leste. Utledet og ikke lagret — en teller i basen ville krevd en
// transaksjon per melding, og ville drevet fra virkeligheten ved første feil.
function fcHasUnread(thread, side) {
  if (!thread) return false;
  const lastAt = Date.parse(String(thread.lastMessageAt || ''));
  if (!Number.isFinite(lastAt)) return false;
  if (String(thread.lastMessageSide || '') === side) return false;
  const readAt = Date.parse(String(side === 'firm' ? thread.firmLastReadAt : thread.clientLastReadAt) || '');
  return !Number.isFinite(readAt) || readAt < lastAt;
}

// Antall uleste meldinger fra motparten. Krever meldingslista, så den brukes bare
// der panelet allerede abonnerer. Hjemkortet klarer seg med `fcHasUnread`.
function fcUnreadCount(messages, thread, side) {
  const readAt = Date.parse(String(side === 'firm' ? thread?.firmLastReadAt : thread?.clientLastReadAt) || '');
  return (messages || []).filter((message) => {
    if (String(message.authorSide || '') === side) return false;
    const at = Date.parse(String(message.createdAt || ''));
    if (!Number.isFinite(at)) return false;
    return !Number.isFinite(readAt) || readAt < at;
  }).length;
}

function FirmClientChat({ businessId, firmId, firmName, counterpartName, side, onClose }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!businessId || !firmId) return undefined;
    return window.PlummeBackend?.subscribeFirmClientChatMessages?.({ businessId, firmId }, setMessages);
  }, [businessId, firmId]);

  // Å åpne panelet ER å lese. Lesemerket settes én gang ved åpning og ikke per
  // melding: motparten trenger å vite at samtalen er sett, ikke nøyaktig hvor
  // langt ned i den man rakk.
  useEffect(() => {
    if (!businessId || !firmId) return;
    void window.PlummeBackend?.markFirmClientChatRead?.({ businessId, firmId, side });
  }, [businessId, firmId, side]);

  useEffect(() => {
    const node = bodyRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages.length]);

  const send = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await window.PlummeBackend?.sendFirmClientChatMessage?.({ businessId, firmId, side, text: body, firmName });
      setText('');
    } catch (error) {
      // En melding som ikke gikk gjennom skal SIES fra om. Uten dette forsvant
      // feilen i konsollen som en ufanget promise, og Enter-tasten sa opplevdes
      // som om den ikke virket i det hele tatt. Teksten blir staaende i feltet,
      // slik at den som skrev slipper aa skrive den paa nytt.
      toast(error?.message || String(error), 'error');
    } finally {
      setSending(false);
    }
  };

  const title = side === 'firm'
    ? (counterpartName || t('chat.client', 'Klienten'))
    : (firmName || t('chat.firm', 'Regnskapsføreren din'));

  return <React.Fragment>
    <div className="chatd-scrim" onClick={onClose} />
    <aside className="chatd" role="dialog" aria-label={title}>
      <div className="chatd-hd">
        <Icon name="mail" size={20} />
        <div className="chatd-hd-title">
          <b>{title}</b>
          <span>{side === 'firm'
            ? t('chat.subFirm', 'Dialog med klienten')
            : t('chat.subClient', 'Dialog med regnskapsføreren din')}</span>
        </div>
        <IconButton name="x" variant="ghost" onClick={onClose} aria-label={t('app.close', 'Lukk')} />
      </div>

      <div className="chatd-body" ref={bodyRef}>
        {messages.length === 0
          ? <div className="chatd-empty">
            {side === 'firm'
              ? t('chat.emptyFirm', 'Ingen meldinger ennå. Skriv til klienten her — de ser den i Plumme.')
              : t('chat.emptyClient', 'Ingen meldinger ennå. Spør regnskapsføreren din om hva du lurer på.')}
          </div>
          : messages.map((message) => {
            const mine = String(message.authorSide || '') === side;
            return <div key={message.id} className={'chatd-msg ' + (mine ? 'me' : 'them')}>
              {!mine && message.authorName ? <div className="chatd-msg-who">{message.authorName}</div> : null}
              <div>{message.text}</div>
              <div className="chatd-time">{fcTime(message.createdAt)}</div>
            </div>;
          })}
      </div>

      <div className="chatd-foot">
        <input
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); void send(); } }}
          placeholder={t('chat.placeholder', 'Skriv en melding …')}
          aria-label={t('chat.placeholder', 'Skriv en melding …')}
        />
        <Button size="sm" disabled={!text.trim() || sending} onClick={() => void send()}>
          {t('chat.send', 'Send')}
        </Button>
      </div>
    </aside>
  </React.Fragment>;
}

Object.assign(window, { FirmClientChat, fcHasUnread, fcUnreadCount, fcTime });
